import { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { useFormikContext } from 'formik';

import { useUsers } from '@/portainer/users/queries/useUsers';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { Option as AccessOption } from '@/react/portainer/access-control/AccessManagement/PorAccessManagementUsersSelector';
import { pluralize } from '@/portainer/helpers/strings';

import { FormControl } from '@@/form-components/FormControl';
import { Option } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Button } from '@@/buttons';
import { FormSection } from '@@/form-components/FormSection';

import { RegistrySelector } from '../../RegistrySelector/RegistrySelector';
import { MultiUserTeamSelector } from '../../MultiUserTeamSelector';

import { DockerRegistryAccessDatatable } from './DockerRegistryAccessDatatable/DockerRegistryAccessDatatable';
import { DockerRegistryFormValues } from './types';

interface Props {
  mode: 'create' | 'edit';
}

/**
 * Docker Registry Policy form fields.
 * Pure field renderer without FormActions - used by both create and edit modes.
 */
export function DockerRegistryFormFields({ mode }: Props) {
  const [tempSelectedRegistry, setTempSelectedRegistry] = useState<
    number | null
  >(null);
  const [selectedOptions, setSelectedOptions] = useState<AccessOption[]>([]);
  const { values, setFieldValue } =
    useFormikContext<DockerRegistryFormValues>();

  const usersQuery = useUsers(false);
  const teamsQuery = useTeams();

  const userTeamOptions: Option<AccessOption>[] = useMemo(() => {
    const users = usersQuery.data || [];
    const teams = teamsQuery.data || [];

    const userOpts: Option<AccessOption>[] = users.map((u) => ({
      value: { Type: 'user', Id: u.Id, Name: u.Username },
      label: u.Username,
    }));
    const teamOpts: Option<AccessOption>[] = teams.map((t) => ({
      value: { Type: 'team', Id: t.Id, Name: t.Name },
      label: t.Name,
    }));

    return [...userOpts, ...teamOpts];
  }, [usersQuery.data, teamsQuery.data]);

  const canAddAccess =
    tempSelectedRegistry !== null && selectedOptions.length > 0;

  const registriesAddedCount = values.accessRows?.length ?? 0;

  return (
    <>
      <FormSection title="Add access">
        <FormControl label="Select registry" required>
          <RegistrySelector
            value={tempSelectedRegistry}
            onChange={setTempSelectedRegistry}
            filter={(registries) => registries.filter((r) => r.Id !== 0)}
          />
        </FormControl>
        <FormControl label="Select users and/or teams" required>
          <MultiUserTeamSelector
            options={userTeamOptions}
            value={selectedOptions}
            onChange={setSelectedOptions}
          />
        </FormControl>

        <div className="form-group">
          <div className="col-sm-12 flex gap-2">
            <Button
              icon={Plus}
              color="primary"
              size="small"
              disabled={!canAddAccess}
              onClick={handleAddAccess}
              data-cy="add-registry-access-button"
              className="!ml-0"
            >
              Add Access
            </Button>
          </div>
        </div>
      </FormSection>

      {/* Access List Datatable */}
      <div className="-mx-4">
        <DockerRegistryAccessDatatable />
      </div>

      {/* Info message */}
      <div className="form-group">
        <div className="col-sm-12">
          <RegistryAccessInfoMessage
            registriesAddedCount={registriesAddedCount}
            mode={mode}
          />
        </div>
      </div>
    </>
  );

  function handleAddAccess() {
    if (!canAddAccess || tempSelectedRegistry === null) return;

    const userIds = selectedOptions
      .filter((o) => o.Type === 'user')
      .map((o) => o.Id);
    const teamIds = selectedOptions
      .filter((o) => o.Type === 'team')
      .map((o) => o.Id);

    const newAccessRows = [...(values.accessRows || [])];
    const existingIndex = newAccessRows.findIndex(
      (row) => row.registryId === tempSelectedRegistry
    );

    if (existingIndex !== -1) {
      newAccessRows[existingIndex] = {
        ...newAccessRows[existingIndex],
        userIds,
        teamIds,
      };
    } else {
      newAccessRows.push({
        id: newAccessRows.length + 1,
        registryId: tempSelectedRegistry,
        userIds,
        teamIds,
      });
    }

    setFieldValue('accessRows', newAccessRows);
    setTempSelectedRegistry(null);
    setSelectedOptions([]);
  }
}

function RegistryAccessInfoMessage({
  registriesAddedCount,
  mode,
}: {
  registriesAddedCount: number;
  mode: 'create' | 'edit';
}) {
  return <TextTip color="blue">{getMessage()}</TextTip>;

  function getMessage() {
    if (registriesAddedCount > 0) {
      return `Registry access configured for ${registriesAddedCount} ${pluralize(
        registriesAddedCount,
        'registry',
        'registries'
      )}.`;
    }
    return `Please add at least one registry to ${
      mode === 'create' ? 'create' : 'update'
    } access.`;
  }
}
